/**
 * The Roles & Permissions catalogue: every permission key, grouped as the role editor shows them.
 * A group belongs to the retail or wholesale module, or is shared by both; which modules a role
 * can open follows from it (utils/moduleAccess.js).
 */

export const PERMISSION_GROUPS = [
  {
    name: 'Dashboard & Analytics',
    module: 'retail',
    permissions: [
      { key: 'view_dashboard', label: 'View Dashboard' },
      { key: 'view_analytics', label: 'View Sales Analytics' },
      { key: 'view_reports', label: 'View Reports' },
      { key: 'view_dashboard_financials', label: 'View Dashboard Financial Metrics' }
    ]
  },
  {
    name: 'POS & Sales Operations',
    module: 'retail',
    permissions: [
      { key: 'access_pos', label: 'Access POS Terminal' },
      { key: 'apply_discounts', label: 'Apply Custom Discounts' },
      { key: 'override_prices', label: 'Change Selling Prices at POS' },
      { key: 'allow_credit_sales', label: 'Make Credit / Part-Payment Sales' },
      { key: 'multi_branch_sales', label: 'Sell Stock From Several Branches' },
      { key: 'view_sales', label: 'View Own Branch Sales' },
      { key: 'view_all_sales', label: 'View All Branches Sales' },
      { key: 'create_sales', label: 'Create New Sales' },
      { key: 'reverse_sales', label: 'Reverse / Cancel Sales' }
    ]
  },
  {
    name: 'Receipts',
    module: 'retail',
    permissions: [
      { key: 'view_receipts', label: 'View, Print & Email Receipts' },
      { key: 'manage_receipts', label: 'Manage Receipt Layout Settings' }
    ]
  },
  {
    name: 'Products & Categories',
    module: 'retail',
    permissions: [
      { key: 'view_products', label: 'View Products Catalog' },
      { key: 'manage_products', label: 'Create/Edit Products' },
      { key: 'view_cost_prices', label: 'See Cost Prices & Profit' },
      { key: 'view_categories', label: 'View Categories' },
      { key: 'manage_categories', label: 'Create/Edit Categories' }
    ]
  },
  {
    name: 'Inventory & Stock Management',
    module: 'retail',
    permissions: [
      { key: 'view_inventory', label: 'View Inventory Stock Levels' },
      { key: 'manage_inventory', label: 'Adjust / Edit Inventory' },
      { key: 'view_stock', label: 'View Stock History' },
      {
        key: 'manage_stock',
        label: 'Record Stock Damages (own branch; they wait for confirmation)'
      },
      {
        key: 'confirm_damages',
        label: 'Confirm Stock Damages (query, reject, confirm, correct, close)'
      },
      { key: 'confirm_own_damages', label: 'Confirm Stock Damages You Recorded Yourself' },
      { key: 'write_off_damages', label: "Write Off Stock Damages as the Business's Loss" },
      {
        key: 'receive_stock',
        label: 'Receive Stock Deliveries (record loads, confirm drops, add products)'
      },
      { key: 'cost_stock', label: 'Cost Stock Deliveries (purchase prices and expenses)' },
      { key: 'approve_stock_transfers', label: 'Approve Stock Transfers' },
      { key: 'receive_stock_transfers', label: 'Receive Stock Transfers (own branch)' },
      {
        key: 'transfer_any_branch',
        label:
          'Acts for Every Branch (stock: transfers, stock requests and damages for every branch)'
      },
      { key: 'request_stock', label: 'Ask for Stock (own branch)' }
    ]
  },
  {
    name: 'Customers & Referrers',
    module: 'retail',
    permissions: [
      { key: 'view_customers', label: 'View Customers' },
      { key: 'create_customers', label: 'Create Customers' },
      { key: 'update_customers', label: 'Update Customers' },
      { key: 'delete_customers', label: 'Delete Customers' },
      { key: 'view_referrers', label: 'View Referrers' },
      { key: 'manage_referrers', label: 'Manage Referrers' }
    ]
  },
  {
    name: 'User & Access Management',
    module: 'shared',
    permissions: [
      { key: 'view_users', label: 'View User Accounts' },
      { key: 'manage_users', label: 'Create/Edit Users' },
      { key: 'manage_roles', label: 'Create/Edit Roles & Assign Roles' }
    ]
  },
  {
    name: 'Vendors, Bills & Invoices',
    module: 'retail',
    permissions: [
      { key: 'view_vendors', label: 'View Vendors' },
      { key: 'manage_vendors', label: 'Manage Vendors' },
      { key: 'view_invoices', label: 'View Invoices' },
      { key: 'manage_invoices', label: 'Manage Invoices' },
      { key: 'view_quotations', label: 'View Quotations' },
      { key: 'manage_quotations', label: 'Manage Quotations' }
    ]
  },
  {
    name: 'Finance & Accounting',
    module: 'retail',
    permissions: [
      { key: 'manage_payments', label: 'Record/Manage Payments' },
      { key: 'confirm_payments', label: 'Confirm Bank / POS Payments' },
      { key: 'view_expenses', label: 'View Expenses' },
      { key: 'manage_expenses', label: 'Manage Expenses' },
      { key: 'approve_expenses', label: 'Approve Expenses (not own)' },
      { key: 'delete_expenses', label: 'Delete Expenses' },
      { key: 'view_bills', label: 'View Bills' },
      { key: 'manage_bills', label: 'Manage Bills' },
      { key: 'view_banks', label: 'View Bank Channels' },
      { key: 'manage_banks', label: 'Manage Bank Channels' },
      { key: 'view_budgets', label: 'View Budgets' },
      { key: 'manage_budgets', label: 'Manage Budgets' },
      { key: 'view_accounting', label: 'View GL & Journal Entries' },
      { key: 'manage_accounting', label: 'Manage GL & Journal Entries' }
    ]
  },
  {
    name: 'Payroll & Performance Tasks',
    module: 'shared',
    permissions: [
      { key: 'manage_payroll', label: 'Manage Payroll & Payslips' },
      { key: 'view_performance_tasks', label: 'View Performance Tasks' },
      { key: 'manage_performance_tasks', label: 'Manage Performance Tasks' },
      { key: 'view_tasks', label: 'View Tasks' },
      { key: 'manage_tasks', label: 'Manage Tasks' }
    ]
  },
  {
    name: 'Settings & System Administration',
    module: 'shared',
    permissions: [
      { key: 'view_branches', label: 'View Branches' },
      { key: 'manage_branches', label: 'Manage Branches' },
      { key: 'manage_settings', label: 'Update System Settings' },
      { key: 'view_audit_logs', label: "View Everyone's Activity / Audit Logs" },
      { key: 'export_data', label: 'Export & Download Reports' },
      { key: 'view_recycle_bin', label: 'View Recycle Bin' },
      { key: 'manage_recycle_bin', label: 'Restore/Purge Recycle Bin' },
      { key: 'reset_retail_module', label: 'Reset Retail Module & Reintegrate Archives' }
    ]
  },
  {
    name: 'Wholesale Module',
    module: 'wholesale',
    permissions: [
      { key: 'ws_view_dashboard', label: 'View Wholesale Dashboard' },
      { key: 'ws_view_orders', label: 'View Wholesale Orders' },
      { key: 'ws_manage_orders', label: 'Manage Wholesale Orders' },
      { key: 'ws_view_customers', label: 'View Wholesale Customers' },
      { key: 'ws_manage_customers', label: 'Manage Wholesale Customers' },
      { key: 'ws_view_pricing', label: 'View Pricing Tiers & Rules' },
      { key: 'ws_manage_pricing', label: 'Manage Pricing Tiers & Rules' },
      { key: 'ws_view_credit', label: 'View Credit Management' },
      { key: 'ws_manage_credit', label: 'Manage Credit & Limits' },
      { key: 'ws_view_companies', label: 'View Companies / Manufacturers' },
      { key: 'ws_manage_companies', label: 'Manage Companies & Rebates' },
      { key: 'ws_view_products', label: 'View Wholesale Products' },
      { key: 'ws_manage_products', label: 'Manage Wholesale Products' },
      { key: 'ws_view_payments', label: 'View Wholesale Payments' },
      { key: 'ws_manage_payments', label: 'Manage Wholesale Payments' },
      { key: 'ws_view_documents', label: 'View Invoices & Documents' },
      { key: 'ws_manage_documents', label: 'Manage Invoices & Documents' },
      { key: 'ws_view_expenses', label: 'View Wholesale Expenses' },
      { key: 'ws_manage_expenses', label: 'Manage Wholesale Expenses' },
      { key: 'ws_approve_expenses', label: 'Approve Wholesale Expenses (not own)' },
      { key: 'ws_view_payroll', label: 'View Wholesale Payroll' },
      { key: 'ws_manage_payroll', label: 'Manage Wholesale Payroll' },
      { key: 'ws_view_accounting', label: 'View Wholesale Accounting' },
      { key: 'ws_manage_accounting', label: 'Manage Wholesale Accounting' },
      { key: 'ws_view_reports', label: 'View Wholesale Reports' },
      { key: 'ws_view_settings', label: 'View Wholesale Settings' },
      { key: 'ws_manage_settings', label: 'Manage Wholesale Settings' },
      { key: 'ws_reset_module', label: 'Reset Wholesale Module & Reintegrate Archives' }
    ]
  }
];
